import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, addDoc, updateDoc, Timestamp, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko, enUS, vi, es, ja, de, fr, id, pt, th, tr, zhCN } from 'date-fns/locale';
import { generateSampleInvoice, pdfToBlob } from '../utils/invoiceGenerator';
import { uploadInvoiceToStorage } from '../utils/firebaseStorage';
import { useSubscription } from '../contexts/SubscriptionContext';

interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  price: number;
}

interface Invoice {
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;
  customerName: string;
  customerCompany: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
  documentType: string;
  documentTitle: string;
}

interface BusinessProfile {
  businessName: string;
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  website?: string;
  businessType?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountHolder?: string;
  logoUrl?: string;
}

const InvoiceCreationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canCreateInvoice, isExpired, isCardOnly } = useSubscription();
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedInvoiceData, setSavedInvoiceData] = useState<any>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [userCards, setUserCards] = useState<any[]>([]);
  
  const userId = getAuth().currentUser?.uid;

  // 🔒 구독 제한 체크
  useEffect(() => {
    if (isExpired) {
      navigate('/paywall', { 
        state: { limitType: 'expired' }
      });
      return;
    }

    if (isCardOnly) {
      navigate('/paywall', { 
        state: { limitType: 'invoice_blocked' }
      });
      return;
    }

    if (!canCreateInvoice) {
      navigate('/paywall', { 
        state: { limitType: 'invoice_limit' }
      });
      return;
    }
  }, [canCreateInvoice, isExpired, isCardOnly, navigate]);

  // 안전한 번역 함수
  const safeT = (key: string, options: any = {}) => {
    try {
      const result = t(key, options);
      return typeof result === 'string' ? result : key;
    } catch (error) {
      console.warn('Translation error for key:', key, error);
      return key;
    }
  };

  const [invoice, setInvoice] = useState<Invoice>({
    invoiceNumber: generateInvoiceNumber(),
    issueDate: new Date(),
    dueDate: new Date(new Date().setDate(new Date().getDate() + 30)),
    customerName: '',
    customerCompany: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    items: [
      { id: Date.now(), description: '', quantity: 1, price: 0 }
    ],
    taxRate: 10,
    notes: '',
    documentType: 'invoice',
    documentTitle: '',
  });

  function generateInvoiceNumber(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}${day}-${random}`;
  }

  const getDateFnsLocale = (langCode: string) => {
    switch (langCode) {
      case 'ko': return ko;
      case 'en': return enUS;
      case 'vi': return vi;
      case 'es': return es;
      case 'ja': return ja;
      case 'de': return de;
      case 'fr': return fr;
      case 'id': return id;
      case 'pt': return pt;
      case 'th': return th;
      case 'tr': return tr;
      case 'zh': return zhCN;
      default: return enUS;
    }
  };

  const formatDate = (date: Date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd', { locale: getDateFnsLocale(i18n.language) });
  };

  useEffect(() => {
    const initializeData = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        // 카드 로드
        const cards = await fetchUserCards();
        if (!cards?.length) {
          alert(safeT('invoiceCreation.noCardsFound'));
          window.location.href = '/dashboard';
          return;
        }
        
        // 첫 번째 카드로 프로필 체크
        const firstCardId = cards[0].id;
        setSelectedCardId(firstCardId);
        
        // 프로필 체크 (카드 ID 직접 전달)
        const hasProfile = await checkBusinessProfile(firstCardId);
        
        if (!hasProfile) {
          alert(safeT('invoiceCreation.businessProfileRequired'));
          window.location.href = '/business-profile';
          return;
        }
        
        setInvoice(prev => ({
          ...prev,
          invoiceNumber: generateInvoiceNumber(),
          issueDate: new Date(),
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          notes: safeT('invoiceCreation.defaultNotes'),
          documentTitle: safeT('invoiceCreation.documentTypeInvoice'),
        }));
      } catch (error) {
        console.error('Error initializing data:', error);
        alert(safeT('invoiceCreation.initializationError'));
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [userId]);

  // 카드 선택이 변경될 때 비즈니스 프로필 다시 가져오기
  useEffect(() => {
    if (selectedCardId && userId) {
      checkBusinessProfile(selectedCardId);
    }
  }, [selectedCardId, userId]);



  const checkBusinessProfile = async (cardId?: string) => {
    try {
      const currentUserId = getAuth().currentUser?.uid;
      if (!currentUserId) return false;
      
      const db = getFirestore();
      const targetCardId = cardId || selectedCardId;
      
      if (targetCardId) {
        // 카드별 프로필 확인
        const cardProfileRef = doc(db, 'users', currentUserId, 'cards', targetCardId, 'businessProfile', 'info');
        const cardProfileSnap = await getDoc(cardProfileRef);
        
        if (cardProfileSnap.exists()) {
          const profileData = cardProfileSnap.data();
          // 앱과 동일한 필드명으로 매핑
          const mappedProfile = {
            businessName: profileData.businessName || '',
            businessType: profileData.businessType || profileData.abn || '',
            businessAddress: profileData.businessAddress || profileData.address || '',
            businessPhone: profileData.businessPhone || profileData.phone || '',
            businessEmail: profileData.businessEmail || profileData.email || '',
            website: profileData.website || '',
            logoUrl: profileData.logoUrl || '',
            bankName: profileData.bankName || '',
            bankAccountNumber: profileData.bankAccountNumber || '',
            bankAccountHolder: profileData.bankAccountHolder || '',
          };
          setBusinessProfile(mappedProfile as BusinessProfile);
          return true;
        }
      }
      
      // 일반 프로필 확인
      const generalProfileRef = doc(db, 'users', currentUserId, 'businessProfiles', 'current');
      const generalProfileSnap = await getDoc(generalProfileRef);
      
      if (generalProfileSnap.exists()) {
        const profileData = generalProfileSnap.data();
        // 앱과 동일한 필드명으로 매핑
        const mappedProfile = {
          businessName: profileData.businessName || '',
          businessType: profileData.businessType || profileData.abn || '',
          businessAddress: profileData.businessAddress || profileData.address || '',
          businessPhone: profileData.businessPhone || profileData.phone || '',
          businessEmail: profileData.businessEmail || profileData.email || '',
          website: profileData.website || '',
          logoUrl: profileData.logoUrl || '',
          bankName: profileData.bankName || '',
          bankAccountNumber: profileData.bankAccountNumber || '',
          bankAccountHolder: profileData.bankAccountHolder || '',
        };
        setBusinessProfile(mappedProfile as BusinessProfile);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking business profile:', error);
      return false;
    }
  };

  // 사용자의 카드 목록을 가져오는 함수
  const fetchUserCards = async () => {
    try {
      const currentUser = getAuth().currentUser;
      const currentUserId = currentUser?.uid;
      
      if (!currentUserId) {
        console.log('fetchUserCards: No current user');
        return;
      }
      
      const db = getFirestore();
      const cardsRef = collection(db, 'users', currentUserId, 'cards');
      const cardsSnapshot = await getDocs(cardsRef);
      
      const cards = cardsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('fetchUserCards: Found cards:', cards);
      setUserCards(cards);
      
      // 첫 번째 카드를 기본 선택
      if (cards.length > 0 && !selectedCardId) {
        setSelectedCardId(cards[0].id);
      }
      
      return cards;
    } catch (error) {
      console.error('fetchUserCards: Error fetching user cards:', error);
      return [];
    }
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0
    };

    setInvoice({
      ...invoice,
      items: [...invoice.items, newItem]
    });
  };

  const removeItem = (id: number) => {
    if (invoice.items.length <= 1) {
      alert(safeT('invoiceCreation.minItemRequired'));
      return;
    }

    setInvoice({
      ...invoice,
      items: invoice.items.filter(item => item.id !== id)
    });
  };

  const normalizeQuantity = (val: string) => {
    const s = String(val).trim();
    if (s === '') return 1;
    const n = parseInt(s.replace(/^0+(?=[1-9])/, ''), 10);
    return isNaN(n) || n < 1 ? 1 : n;
  };

  const parsePriceInput = (val: string): number | string => {
    let s = String(val).replace(/[^0-9.]/g, '');
    if (s === '') return '';
    const parts = s.split('.');
    if (parts.length > 2) s = parts[0] + '.' + parts.slice(1).join('');
    if (s === '.' || s.endsWith('.')) return s;
    const n = parseFloat(s);
    return isNaN(n) || n < 0 ? '' : n;
  };

  const updateItem = (id: number, field: keyof InvoiceItem, value: any) => {
    const updatedItems = invoice.items.map(item => {
      if (item.id === id) {
        let parsedValue: any = value;
        if (field === 'quantity') parsedValue = normalizeQuantity(String(value));
        else if (field === 'price') parsedValue = parsePriceInput(String(value));
        return { ...item, [field]: parsedValue };
      }
      return item;
    });

    setInvoice({
      ...invoice,
      items: updatedItems
    });
  };

  const calculateSubtotal = () => {
    return invoice.items.reduce((sum, item) => sum + (item.quantity * (Number(item.price) || 0)), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (invoice.taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatCurrency = (amount: number) => {
    const numericAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    const currencySymbol = safeT('common.currencySymbol') || '$';
    
    let formattedAmount;
    const currentLanguage = i18n.language;
    if (currentLanguage === 'ko') {
      formattedAmount = numericAmount.toLocaleString('ko-KR');
    } else if (currentLanguage === 'en') {
      formattedAmount = numericAmount.toLocaleString('en-US');
    } else {
      formattedAmount = numericAmount.toLocaleString();
    }
    
    return `${currencySymbol}${formattedAmount}`;
  };

  const validateInvoice = () => {
    if (!invoice.customerName || !invoice.customerName.trim()) {
      alert(safeT('invoiceCreation.customerNameRequired'));
      return false;
    }
    
    if (invoice.customerName.trim().length < 2) {
      alert(safeT('invoiceCreation.customerNameTooShort'));
      return false;
    }

    for (const item of invoice.items) {
      if (!item.description || !item.description.trim()) {
        alert(safeT('invoiceCreation.itemDescriptionRequired'));
        return false;
      }
      if (item.quantity <= 0) {
        alert(safeT('invoiceCreation.itemQuantityInvalid'));
        return false;
      }
      if (item.price < 0) {
        alert(safeT('invoiceCreation.itemPriceInvalid'));
        return false;
      }
    }

    if (calculateTotal() <= 0) {
      alert(safeT('invoiceCreation.totalAmountInvalid'));
      return false;
    }

    return true;
  };

  const handleGenerateInvoice = async () => {
    console.log('handleGenerateInvoice called');
    
    if (!invoice.customerName || !invoice.customerName.trim()) {
      console.error('InvoiceCreationPage: 고객명이 없습니다');
      alert(safeT('invoiceCreation.customerNameRequired'));
      return;
    }
    
    if (!userId) {
      alert(safeT('invoiceCreation.userIdMissing'));
      console.error('InvoiceCreationPage: userId missing');
      return;
    }
    
    if (!businessProfile?.businessName) {
      console.error('InvoiceCreationPage: businessProfile missing', businessProfile);
      alert(safeT('invoiceCreation.businessProfileRequired'));
      return;
    }

    if (!validateInvoice()) {
      console.error('InvoiceCreationPage: Data validation failed');
      return;
    }

    setSaving(true);
    
    try {
      console.log('PDF generation started');
      
      // 비즈니스 프로필 데이터 준비
      const businessProfileForPdf = {
        businessName: businessProfile?.businessName || '',
        address: businessProfile?.businessAddress || '',
        phone: businessProfile?.businessPhone || '',
        email: businessProfile?.businessEmail || '',
        website: businessProfile?.website || '',
        abn: businessProfile?.businessType || '',
        bankName: businessProfile?.bankName || '',
        bankAccountNumber: businessProfile?.bankAccountNumber || '',
        bankAccountHolder: businessProfile?.bankAccountHolder || '',
        logoUrl: businessProfile?.logoUrl || ''
      };
      
      // PDF 생성 (웹 버전 방식)
      console.log('🔍 InvoiceCreationPage: Generating PDF with language:', i18n.language);
      const pdfBlob = await generateSampleInvoice(invoice, businessProfileForPdf, i18n.language || 'en');
      
      // Firestore에 인보이스 문서 저장 (invoiceId 확보)
      const db = getFirestore();
      const now = new Date();
      
      // 앱과 동일한 경로로 저장: users/{userId}/cards/{cardId}/invoices
      if (!selectedCardId) {
        throw new Error('카드가 선택되지 않았습니다.');
      }
      
      const invoiceRef = await addDoc(collection(db, 'users', userId, 'cards', selectedCardId, 'invoices'), {
        ...invoice,
        cardId: selectedCardId, // 카드 ID 추가
        createdAt: now,
        updatedAt: now,
      });
      const invoiceId = invoiceRef.id;

      // PDF를 Firebase Storage에 업로드
      const downloadUrl = await uploadInvoiceToStorage(
        pdfBlob, // 생성된 PDF Blob
        userId,
        invoiceId // Firestore에 저장된 문서 ID
      );

      // Firestore에 pdfUrl 업데이트
      await updateDoc(doc(db, 'users', userId, 'cards', selectedCardId, 'invoices', invoiceId), {
        pdfUrl: downloadUrl,
        pdfPath: `invoices/${userId}/invoice_${invoiceId}.pdf`,
        pdfFileName: `invoice_${invoiceId}.pdf`
      });

      console.log('✅ InvoiceCreationPage: PDF uploaded to Firebase Storage:', downloadUrl);

      // 성공 메시지 표시
      alert(safeT('invoiceCreation.saveComplete'));
      
      // 인보이스 리스트 페이지로 이동
      window.location.href = '/invoices';
      
    } catch (error) {
      console.error('Invoice creation error:', error);
      alert(safeT('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {safeT('invoiceCreation.newInvoice')}
            </h1>
            <p className="text-gray-600">
              {safeT('invoiceCreation.subtitle')}
            </p>
          </div>

                     {/* Card Selection - 자동으로 첫 번째 카드 선택 */}
           {userCards.length > 0 && (
             <div className="mb-8">
               <h3 className="text-lg font-semibold text-gray-900 mb-4">
                 {safeT('invoiceCreation.selectedCard')}
               </h3>
               <div className="p-4 border-2 border-blue-500 bg-blue-50 rounded-lg">
                 <div className="text-center">
                   <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg mx-auto mb-2 flex items-center justify-center">
                     <span className="text-white font-bold text-lg">
                       {userCards[0]?.name ? userCards[0].name.charAt(0).toUpperCase() : 'C'}
                     </span>
                   </div>
                   <h3 className="font-medium text-gray-900">
                     {userCards[0]?.name || safeT('businessProfile.defaultCard')}
                   </h3>
                   <p className="text-sm text-gray-500">
                     {userCards[0]?.company || userCards[0]?.title || safeT('businessProfile.noCompany')}
                   </p>
                 </div>
               </div>
             </div>
           )}

           {/* Business Profile Summary */}
           <div className="mb-8 p-4 bg-blue-50 rounded-lg">
             <h3 className="text-lg font-semibold text-gray-900 mb-4">
               {safeT('invoiceCreation.businessInfo')}
             </h3>
             {businessProfile ? (
               <div className="text-center">
                 {businessProfile.logoUrl && (
                   <img 
                     src={businessProfile.logoUrl} 
                     alt="Business Logo"
                     className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
                   />
                 )}
                 <h4 className="font-semibold text-gray-900">{businessProfile.businessName}</h4>
                 {businessProfile.businessEmail && (
                   <p className="text-gray-600">{businessProfile.businessEmail}</p>
                 )}
                 {businessProfile.businessPhone && (
                   <p className="text-gray-600">{businessProfile.businessPhone}</p>
                 )}
                 {businessProfile.businessAddress && (
                   <p className="text-gray-600">{businessProfile.businessAddress}</p>
                 )}
               </div>
             ) : (
               <div className="text-center text-red-600">
                 <p>{safeT('invoiceCreation.businessProfileRequired')}</p>
                 <button 
                   onClick={() => window.location.href = '/business-profile'}
                   className="mt-2 text-blue-600 hover:underline"
                 >
                   {safeT('invoiceCreation.setupBusinessProfile')}
                 </button>
               </div>
             )}
           </div>

          {/* Document Type Selection */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceCreation.documentTitle')}
            </h3>
            <select
              value={invoice.documentType}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'custom') {
                  setInvoice({ ...invoice, documentType: 'custom', documentTitle: '' });
                } else {
                  const documentTypeKeys: { [key: string]: string } = {
                    'invoice': 'invoiceCreation.documentTypeInvoice',
                    'quotation': 'invoiceCreation.documentTypeQuotation',
                    'taxInvoice': 'invoiceCreation.documentTypeTaxInvoice',
                    'estimate': 'invoiceCreation.documentTypeEstimate',
                    'receipt': 'invoiceCreation.documentTypeReceipt'
                  };
                  
                  const translationKey = documentTypeKeys[value] || 'invoiceCreation.documentTypeInvoice';
                  const translatedTitle = safeT(translationKey);
                  
                  setInvoice({ ...invoice, documentType: value, documentTitle: translatedTitle });
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="invoice">{safeT('invoiceCreation.documentTypeInvoice')}</option>
              <option value="quotation">{safeT('invoiceCreation.documentTypeQuotation')}</option>
              <option value="taxInvoice">{safeT('invoiceCreation.documentTypeTaxInvoice')}</option>
              <option value="estimate">{safeT('invoiceCreation.documentTypeEstimate')}</option>
              <option value="receipt">{safeT('invoiceCreation.documentTypeReceipt')}</option>
              <option value="custom">{safeT('invoiceCreation.documentTypeCustom')}</option>
            </select>
            
            {invoice.documentType === 'custom' && (
              <input
                type="text"
                value={invoice.documentTitle}
                onChange={(e) => setInvoice({ ...invoice, documentTitle: e.target.value })}
                placeholder={safeT('invoiceCreation.documentTitlePlaceholder')}
                className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          </div>

          {/* Invoice Details */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceCreation.invoiceDetails')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.invoiceNumber')}
                </label>
                <input
                  type="text"
                  value={invoice.invoiceNumber}
                  onChange={(e) => setInvoice({ ...invoice, invoiceNumber: e.target.value })}
                  placeholder={safeT('placeholders.invoiceNumber')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.issueDate')}
                </label>
                <input
                  type="date"
                  value={formatDate(invoice.issueDate)}
                  onChange={(e) => setInvoice({ ...invoice, issueDate: new Date(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.dueDate')}
                </label>
                <input
                  type="date"
                  value={formatDate(invoice.dueDate)}
                  onChange={(e) => setInvoice({ ...invoice, dueDate: new Date(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceCreation.customerInfo')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.customerName')} *
                </label>
                <input
                  type="text"
                  value={invoice.customerName}
                  onChange={(e) => setInvoice({ ...invoice, customerName: e.target.value })}
                  placeholder={safeT('placeholders.customerName')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.customerCompany')}
                </label>
                <input
                  type="text"
                  value={invoice.customerCompany}
                  onChange={(e) => setInvoice({ ...invoice, customerCompany: e.target.value })}
                  placeholder={safeT('placeholders.customerCompany')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.customerEmail')}
                </label>
                <input
                  type="email"
                  value={invoice.customerEmail}
                  onChange={(e) => setInvoice({ ...invoice, customerEmail: e.target.value })}
                  placeholder={safeT('placeholders.customerEmail')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.customerPhone')}
                </label>
                <input
                  type="tel"
                  value={invoice.customerPhone}
                  onChange={(e) => setInvoice({ ...invoice, customerPhone: e.target.value })}
                  placeholder={safeT('placeholders.customerPhone')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {safeT('invoiceCreation.customerAddress')}
                </label>
                <textarea
                  value={invoice.customerAddress}
                  onChange={(e) => setInvoice({ ...invoice, customerAddress: e.target.value })}
                  placeholder={safeT('placeholders.customerAddress')}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('invoiceCreation.notes')}
            </h3>
            <textarea
              value={invoice.notes}
              onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
              placeholder={safeT('placeholders.notes')}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Invoice Items */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {safeT('invoiceCreation.invoiceItems')}
              </h3>
              <button
                onClick={addItem}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {safeT('invoiceCreation.addItem')}
              </button>
            </div>
            
            <div className="space-y-4">
              {invoice.items.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-medium text-gray-900">
                      {safeT('invoiceCreation.itemNumber', { index: index + 1 })}
                    </h4>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceCreation.itemDescription')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                        placeholder={safeT('placeholders.itemDescription')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceCreation.quantity')}
                      </label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {safeT('invoiceCreation.unitPrice')}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">
                          {safeT('common.currencySymbol')}
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.price}
                          onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 text-right">
                    <span className="text-sm text-gray-600">
                      {safeT('invoiceCreation.amount')}: 
                    </span>
                    <span className="ml-2 font-semibold text-blue-600">
                      {formatCurrency(item.quantity * item.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">{safeT('invoiceCreation.subtotal')}</span>
                  <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">
                    {safeT('invoiceCreation.tax')} (
                    <input
                      type="number"
                      value={invoice.taxRate}
                      onChange={(e) => setInvoice({ ...invoice, taxRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center"
                    />
                    %)
                  </span>
                  <span className="font-medium">{formatCurrency(calculateTax())}</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">
                      {safeT('invoiceCreation.total')}
                    </span>
                    <span className="text-lg font-semibold text-blue-600">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="text-center">
            <button
              onClick={handleGenerateInvoice}
              disabled={saving}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {safeT('invoiceCreation.saving')}
                </div>
              ) : (
                safeT('invoiceCreation.generateInvoice')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCreationPage;
